import { z } from 'zod';
import { isAfter } from 'date-fns';

import {
  convertSendAtToDBValue,
  convertSendAtToFormValue
} from 'pages/dashboard/overview/events/schemas/commonUtils';
import { parseUTCDate } from 'utils/dateTimeUtils';

/**
 * Schedule Publish Modal Schema
 *
 * Form Format (browser timezone):
 * {
 *   scheduledAt: Date;  // Date object in browser's timezone
 *   timezoneCode: string;  // IANA timezone code
 * }
 *
 * DB Format (UTC):
 * {
 *   scheduledAt: string;  // UTC string (e.g., '2024-01-15 10:30:00')
 *   timezoneCode: string;  // IANA timezone code (used for conversion)
 * }
 *
 * The schema transforms Date -> UTC string using the selected timezone.
 *
 * Note: Communications conflict validation is handled at the component level
 * to enable custom error UI with "See details" link.
 */

export const createSchedulePublishModalSchema = ({
  datetimeScheduled
}: {
  datetimeScheduled?: string | null;
}) =>
  z
    .object({
      scheduledAt: z.date({
        required_error: 'Date and time are required'
      }),
      timezoneCode: z.string()
    })
    .transform((data) => ({
      // Transform: Convert Date (browser timezone) -> UTC string for DB using selected timezone
      scheduledAt: convertSendAtToDBValue(data.scheduledAt, data.timezoneCode),
      timezoneCode: data.timezoneCode
    }))
    // Validation: Must be in the future
    .refine(
      (data) => {
        const scheduledAtUTC = parseUTCDate(data.scheduledAt);
        const now = new Date();
        return isAfter(scheduledAtUTC, now);
      },
      {
        message: 'Scheduled time must be in the future',
        path: ['scheduledAt']
      }
    )
    // Validation: Must be same as or before event start
    .refine(
      (data) => {
        if (!datetimeScheduled) {
          return true;
        }

        const scheduledAtUTC = parseUTCDate(data.scheduledAt);
        const eventStartUTC = parseUTCDate(datetimeScheduled);

        // Allow same time or before (not after)
        return !isAfter(scheduledAtUTC, eventStartUTC);
      },
      {
        message: 'Publish time must be the same as or before the event start date and time.',
        path: ['scheduledAt']
      }
    );

// Form input type: What the form receives (Date objects in browser timezone)
export type SchedulePublishModalFormInput = z.input<
  ReturnType<typeof createSchedulePublishModalSchema>
>;

// Form output type: What gets sent to the backend (UTC strings)
export type SchedulePublishModalFormValues = z.infer<
  ReturnType<typeof createSchedulePublishModalSchema>
>;

/**
 * Convert database values to form input values
 * Transforms UTC strings from DB -> Date objects in browser timezone for the form
 *
 * @param scheduledAt - UTC string from database
 * @param timezoneCode - Timezone code for proper conversion
 * @returns Form input values with Date objects
 */
export const convertSchedulePublishDBToFormData = (
  scheduledAt: string | null | undefined,
  timezoneCode: string
): SchedulePublishModalFormInput | null => {
  if (!scheduledAt) {
    return null;
  }

  return {
    scheduledAt: convertSendAtToFormValue(scheduledAt, timezoneCode),
    timezoneCode
  };
};
